import axios from 'axios';
import { api } from '@/lib/api';
import { queryClient } from '@/lib/query-client';
import { createSandboxAdapter } from './adapter';
import { sandboxRoutes } from './routes';
import { dropStore, getStore, resetStore } from './store';

/**
 * Turning the sandbox on and off. On means every `/api/v3` call the app
 * makes is answered from the in-browser store; off restores the network.
 * The flag lives in sessionStorage so a reload stays in the sandbox and a
 * new tab starts real.
 */

const FLAG_KEY = 'bce.sandbox';
const RETURN_KEY = 'bce.sandbox.returnTo';
const ENTER_PARAM = 'sandbox';

let realAdapter: ReturnType<typeof axios.getAdapter> | null = null;
let installed = false;

export function isSandbox(): boolean {
  try {
    return sessionStorage.getItem(FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function install(): void {
  if (installed) return;
  realAdapter = axios.getAdapter(axios.defaults.adapter);
  const adapter = createSandboxAdapter(realAdapter, sandboxRoutes, getStore);
  axios.defaults.adapter = adapter;
  api.defaults.adapter = adapter;
  installed = true;
}

function uninstall(): void {
  if (!installed || !realAdapter) return;
  axios.defaults.adapter = realAdapter;
  api.defaults.adapter = realAdapter;
  installed = false;
}

/** Called once on boot: honors the session flag, or `?sandbox=1` on the URL. */
export function installSandboxIfActive(): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(ENTER_PARAM) === '1') {
      sessionStorage.setItem(FLAG_KEY, '1');
      url.searchParams.delete(ENTER_PARAM);
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }
  } catch {
    // No URL to read in a test; the flag alone decides.
  }
  if (isSandbox()) install();
}

/** Switches the sandbox on and remembers where the person was. */
export function enterSandbox(returnTo: string): void {
  if (isSandbox()) return;
  try {
    sessionStorage.setItem(FLAG_KEY, '1');
    sessionStorage.setItem(RETURN_KEY, returnTo);
  } catch {
    // Without storage the sandbox still works for this page load.
  }
  install();
  queryClient.clear();
}

/** Switches it off, forgets everything done inside, and says where to go back to. */
export function leaveSandbox(): string {
  let returnTo = '/events';
  try {
    returnTo = sessionStorage.getItem(RETURN_KEY) ?? returnTo;
    sessionStorage.removeItem(FLAG_KEY);
    sessionStorage.removeItem(RETURN_KEY);
  } catch {
    // Nothing stored.
  }
  dropStore();
  uninstall();
  queryClient.clear();
  return returnTo;
}

/** Puts the seed back. */
export function resetSandbox(): void {
  resetStore();
  queryClient.clear();
}

/** The sandbox's sample ids, for tours that need a record to open. */
export function sandboxIds(): { eventId: string; programId: string; staffContactId: string; shipmentId: string; organizationId: string; portalEventId: string } {
  const store = getStore();
  return {
    eventId: store.eventId,
    programId: store.programId,
    staffContactId: store.staffContacts[0]?._id ?? '',
    shipmentId: store.shipments[0]?._id ?? '',
    organizationId: store.portal.organizationId,
    portalEventId: store.portal.eventId,
  };
}
